// ADR 25.9.26f §2 — the window classifier the first frame runs before the wasm
// has arrived.
//
// `rust-core/src/window_size/` owns the decision, and the wasm facade still
// exports it for everything on the wasm side. This twin exists because the
// shell must classify the window in its first frame, and 25.9.26f §1 keeps
// that frame's module graph off the facade's top-level `await`. It is one of
// the two sanctioned twins `portsRetired.test.ts` names (AC11); a third fails.
//
// The thresholds are *read* from the canonical table, never restated: a named
// JSON import, so Vite's JSON named exports leave the `size_class` and
// `devices` rows out of the entry. `breakpoints.test.ts` replays every row of
// that same table against these functions and against the facade (AC2).
//
// The inputs are non-negative integers, as the wasm's `u32` was; `measure()`
// in `useWindowClass.ts` rounds before it asks.

import { thresholds_dp } from '../../../rust-core/src/window_size/window_size_classes.json'
import type { NavChrome, PaneLayout, WindowSizeClass } from '../wasm'

/** ADR 30.9.26i §4 — the compact/medium edge, read by the text scale's desktop edge. */
export const MEDIUM_MIN_DP = thresholds_dp.medium_min

/** Which band a window's width falls into. */
export function windowSizeClass(widthDp: number): WindowSizeClass {
  if (widthDp < thresholds_dp.medium_min) return 'compact'
  if (widthDp < thresholds_dp.expanded_min) return 'medium'
  return 'expanded'
}

/** Two panes need Expanded width **and** a real height. */
export function paneLayout(widthDp: number, heightDp: number): PaneLayout {
  const wideEnough = windowSizeClass(widthDp) === 'expanded'
  return wideEnough && heightDp >= thresholds_dp.list_detail_min_height
    ? 'list_detail'
    : 'single_pane'
}

/** Which navigation chrome a window of this width wears. */
export function navChrome(widthDp: number): NavChrome {
  return windowSizeClass(widthDp) === 'compact' ? 'bottom_bar' : 'rail'
}
